import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';

const AuthContext = createContext(null);

// Routes that need auth — skip /api/me on public pages to avoid 401 console noise
const AUTH_ROUTES = ['/dashboard', '/admin', '/login', '/auth', '/invite'];
function needsAuth() {
  return AUTH_ROUTES.some(r => window.location.pathname.startsWith(r));
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(() => needsAuth());
  const abortRef = useRef(null);

  const fetchUser = useCallback(async () => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    try {
      const res = await fetch('/api/me', { credentials: 'same-origin', signal: controller.signal });
      if (res.ok) {
        const data = await res.json();
        setUser(data);
        return data;
      } else {
        setUser(null);
        return null;
      }
    } catch (e) {
      if (e.name === 'AbortError') return null;
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (needsAuth()) {
      fetchUser();
    }
    return () => abortRef.current?.abort();
  }, [fetchUser]);

  const logout = useCallback(async () => {
    abortRef.current?.abort();
    await fetch('/api/auth/logout', {
      method: 'POST',
      credentials: 'same-origin',
    });
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, logout, refetchUser: fetchUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
